import { useEffect, useMemo, useState } from 'react';
import type { Section } from '../../types/report';

interface ReportTocProps {
  sections: Section[];
  /** Heading for the opening group (sections before the first act-divider). */
  openingLabel?: string;
}

interface TocGroup {
  heading: string;
  items: { id: string; label: string; num: string }[];
}

/** Build the left-rail TOC. Chapters are defined by the report's act-divider
 * bands (the same structure the mock uses): each divider starts a new group and
 * its title becomes the heading; sections before the first divider fall under
 * the opening group. Dividers themselves are not clickable TOC entries. */
export function ReportToc({ sections, openingLabel = 'Frame' }: ReportTocProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const groups = useMemo<TocGroup[]>(() => {
    const out: TocGroup[] = [{ heading: openingLabel, items: [] }];
    let n = 0;
    for (const s of sections) {
      if (s.kind === 'cover' || s.kind === 'closing-frame') continue; // not TOC entries
      if (s.kind === 'act-divider') {
        // prefer the short chapter eyebrow ("II · The Segments in Detail") over
        // the long band title for the rail heading.
        const eyebrow = s.blocks?.[0]?.data?.eyebrow as string | undefined;
        out.push({ heading: eyebrow || s.title, items: [] });
        continue;
      }
      n += 1;
      out[out.length - 1].items.push({
        id: s.id,
        label: s.title,
        num: String(n).padStart(2, '0'),
      });
    }
    return out.filter((g) => g.items.length > 0);
  }, [sections, openingLabel]);

  // scroll-spy: highlight the section nearest the top of the viewport
  useEffect(() => {
    const ids = sections
      .filter((s) => s.kind !== 'act-divider' && s.kind !== 'cover')
      .map((s) => s.id);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (els.length === 0 || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '0px 0px -70% 0px', threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <nav className="report-toc-rail" aria-label="Table of contents">
      <div className="report-toc-rail-label">Contents</div>
      {groups.map((g) => (
        <div key={g.heading} className="report-nav-group">
          <div className="report-nav-heading">{g.heading}</div>
          {g.items.map((it) => (
            <a
              key={it.id}
              href={`#${it.id}`}
              className={`report-nav-link${activeId === it.id ? ' active' : ''}`}
            >
              <span className="report-nav-num">{it.num}</span>
              <span className="report-nav-link-label">{it.label}</span>
            </a>
          ))}
        </div>
      ))}
    </nav>
  );
}
